import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FileTextIcon } from "@phosphor-icons/react";
import { SiteIcon } from "@libs/zapi-ui/ds/components/site-icon";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
import { documentOrigin, formatValueParts, formulaParts, sourceBucket, textWithLinks } from "./ledgerFormat";
import { SOURCE_LABEL_KEY } from "./ledgerLabels";
import { FormulaParts } from "./_FormulaParts";
import { BODY_TEXT, LINK } from "./_LedgerParts";

const stopRowToggle = (event: MouseEvent) => event.stopPropagation();

export function Formula({
  formula,
  byKey,
  onReveal,
  equals
}: {
  formula: string;
  byKey: Map<string, TLedgerFigure>;
  onReveal: (key: string) => void;
  equals?: string;
}) {
  return (
    <div className="flex flex-wrap content-start items-center gap-1.5">
      <FormulaParts parts={formulaParts(formula)} byKey={byKey} onReveal={onReveal} />
      {equals !== undefined && (
        <span className="inline-flex items-center gap-1.5">
          <span className="typo-label-md ps-1 text-muted-foreground">=</span>
          <span className="typo-label-md tabular-nums text-foreground">{equals}</span>
        </span>
      )}
    </div>
  );
}

// The number never breaks; a unit that does not fold into it ("per share", "months") may wrap
// under it.
export function ValueCell({ figure }: { figure: TLedgerFigure }) {
  const { t } = useTranslation("projectHome");
  const { number, qualifier } = formatValueParts(figure.value, figure.unit, figure.dp);
  return (
    <>
      <span className="tabular-nums">
        <span className="whitespace-nowrap">{number}</span>
        {qualifier && <span> {qualifier}</span>}
      </span>
      {figure.error && (
        <span className="typo-caption text-destructive" data-testid={`ledger-row-${figure.key}-error`}>
          {t("modelLedger.valueNotComputed")}
        </span>
      )}
      {figure.unverified && (
        <span className="typo-caption text-primary-text" data-testid={`ledger-row-${figure.key}-unverified`}>
          {t("modelLedger.unverified")}
        </span>
      )}
    </>
  );
}

export function SourceCell({ figure }: { figure: TLedgerFigure }) {
  const { t } = useTranslation("projectHome");
  const bucket = sourceBucket(figure);
  if (bucket === "web" && figure.source?.url) {
    return (
      <a
        href={figure.source.url}
        target="_blank"
        rel="noreferrer"
        onClick={stopRowToggle}
        data-testid={`ledger-row-${figure.key}-source-link`}
        className={cn(LINK, "typo-caption max-w-full")}
      >
        <SiteIcon href={figure.source.url} />
        <span className="truncate">{figure.source.title || t("modelLedger.sourced")}</span>
      </a>
    );
  }
  if (bucket === "document" || bucket === "brief") {
    const label =
      bucket === "document"
        ? documentOrigin(figure.source?.ref ?? null).fileName ?? t("modelLedger.originDocument")
        : t("modelLedger.sourceBrief");
    return (
      <span className="typo-caption flex min-w-0 items-center gap-1.5 text-muted-foreground">
        <FileTextIcon className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </span>
    );
  }
  return <span className="typo-caption block truncate text-muted-foreground">{t(SOURCE_LABEL_KEY[bucket])}</span>;
}

export function SourceText({ text }: { text: string }) {
  return (
    <p className={cn(BODY_TEXT, "max-w-[80ch] wrap-break-word")}>
      {textWithLinks(text).map((part, i) =>
        part.kind === "link" ? (
          <a
            key={i}
            href={part.url}
            target="_blank"
            rel="noreferrer"
            className="break-all text-primary-text underline underline-offset-2"
          >
            {part.url}
          </a>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </p>
  );
}
